import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Alert, Select, MenuItem, FormControl, InputLabel, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import billingApi from '../../../../services/billingApi';
import FeaturePalette from './FeaturePalette';
import PlanCard from './PlanCard';
import AdminPageSkeleton from '../../../../components/shared/AdminPageSkeleton';

function getInitialRestrictedValue(featureType, constraints) {
  switch (featureType) {
    case 'numeric': return 0;
    case 'boolean': return constraints?.inverted ? true : false;
    case 'option_set': return [];
    default: return false;
  }
}

export default function PlanBuilder() {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addPlanDialog, setAddPlanDialog] = useState(false);
  const [addPlanForm, setAddPlanForm] = useState({
    planId: '', name: '', price_cents: 0, interval_type: 'month', trial_days: 0
  });
  const [activeDrag, setActiveDrag] = useState(null);
  const [workflows, setWorkflows] = useState([]);

  useEffect(() => {
    billingApi.get('/api/admin/workflows/definitions')
      .then(data => setWorkflows(data.definitions || []))
      .catch(() => {});
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await billingApi.get('/api/admin/pricing-manager/load');
      setProducts(data.products || []);
      if (!selectedProductId && data.products?.length > 0) {
        const withConfig = data.products.find(p => p.draftConfig || p.activeConfig);
        setSelectedProductId((withConfig || data.products[0]).id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProductId]);

  useEffect(() => { loadAll(); }, []);

  const product = products.find(p => p.id === selectedProductId);
  const config = product?.draftConfig || product?.activeConfig;
  const isDraft = config?.status === 'draft';
  const catalog = product?.catalog?.groups || [];
  const allCatalogEntries = catalog.flatMap(g => g.features || []);

  // Collect all restricted feature keys across all plans
  const allRestrictedKeys = new Set();
  for (const plan of config?.plans || []) {
    for (const key of Object.keys(plan.features || {})) {
      allRestrictedKeys.add(key);
    }
  }

  async function handleFeatureChange(planId, featureKey, value) {
    if (!config) return;
    try {
      await billingApi.put(`/api/admin/pricing-configs/${config.id}/plans/${planId}/features`, {
        features: { [featureKey]: value }
      });
      setProducts(prev => prev.map(p => {
        if (p.id !== selectedProductId) return p;
        const updateConfig = (c) => {
          if (!c || c.id !== config.id) return c;
          return { ...c, plans: c.plans.map(plan =>
            plan.id === planId ? { ...plan, features: { ...plan.features, [featureKey]: value } } : plan
          )};
        };
        return { ...p, draftConfig: updateConfig(p.draftConfig), activeConfig: updateConfig(p.activeConfig) };
      }));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleFeatureRemove(planId, featureKey) {
    if (!config) return;
    try {
      await billingApi.del(`/api/admin/pricing-configs/${config.id}/plans/${planId}/features/${encodeURIComponent(featureKey)}`);
      setProducts(prev => prev.map(p => {
        if (p.id !== selectedProductId) return p;
        const updateConfig = (c) => {
          if (!c || c.id !== config.id) return c;
          return { ...c, plans: c.plans.map(plan => {
            if (plan.id !== planId) return plan;
            const { [featureKey]: _, ...rest } = plan.features || {};
            return { ...plan, features: rest };
          })};
        };
        return { ...p, draftConfig: updateConfig(p.draftConfig), activeConfig: updateConfig(p.activeConfig) };
      }));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUpdatePlan(planId, updates) {
    if (!config) return;
    try {
      await billingApi.put(`/api/admin/pricing-configs/${config.id}/plans/${planId}`, updates);
      setProducts(prev => prev.map(p => {
        if (p.id !== selectedProductId) return p;
        const updateConfig = (c) => {
          if (!c || c.id !== config.id) return c;
          return { ...c, plans: c.plans.map(plan =>
            plan.id === planId ? { ...plan, ...updates } : plan
          )};
        };
        return { ...p, draftConfig: updateConfig(p.draftConfig), activeConfig: updateConfig(p.activeConfig) };
      }));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDeletePlan(planId) {
    if (!config || !window.confirm('Delete this plan?')) return;
    try {
      await billingApi.del(`/api/admin/pricing-configs/${config.id}/plans/${planId}`);
      await loadAll();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAddPlan() {
    if (!config) return;
    try {
      await billingApi.post(`/api/admin/pricing-configs/${config.id}/plans`, addPlanForm);
      setAddPlanDialog(false);
      setAddPlanForm({ planId: '', name: '', price_cents: 0, interval_type: 'month', trial_days: 0 });
      await loadAll();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleValidate() {
    if (!config) return;
    try {
      const data = await billingApi.post(`/api/admin/pricing-configs/${config.id}/validate`);
      if (data.valid) setSuccess('Validation passed — no tier-ordering issues');
      else setError('Violations: ' + data.violations.map(v => v.message).join('; '));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleActivate() {
    if (!config || !window.confirm('Activate? This updates live plans.')) return;
    try {
      await billingApi.post(`/api/admin/pricing-configs/${config.id}/activate`);
      setSuccess('Activated — live plans updated');
      await loadAll();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleClone() {
    if (!config) return;
    try {
      await billingApi.post(`/api/admin/pricing-configs/${config.id}/clone`, { version_label: 'New draft' });
      setSuccess('Cloned as new draft');
      await loadAll();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleNewDraft() {
    if (!selectedProductId) return;
    try {
      await billingApi.post('/api/admin/pricing-configs', {
        product_id: selectedProductId, version_label: 'New draft'
      });
      await loadAll();
    } catch (err) {
      setError(err.message);
    }
  }

  function handleDragStart(event) {
    setActiveDrag(event.active.data.current);
  }

  function handleDragEnd(event) {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || !active.data.current?.type) return;

    const featureData = active.data.current;
    const dropTarget = over.data.current;

    if (featureData.type === 'feature' && dropTarget?.planId) {
      const initialValue = getInitialRestrictedValue(featureData.featureType, featureData.constraints);
      handleFeatureChange(dropTarget.planId, featureData.featureKey, initialValue);
    }
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <AdminPageSkeleton bare rows={5} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Plan Builder</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Product</InputLabel>
            <Select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} label="Product">
              {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>

          {config && (
            <Chip
              label={config.status}
              color={config.status === 'active' ? 'success' : 'warning'}
              size="small"
            />
          )}

          {isDraft && <Button size="small" onClick={handleValidate}>Validate</Button>}
          {isDraft && <Button size="small" variant="contained" color="success" onClick={handleActivate}>Activate</Button>}
          {config?.status === 'active' && <Button size="small" onClick={handleClone}>Clone to Draft</Button>}
          {!config && <Button size="small" onClick={handleNewDraft}>New Draft</Button>}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {!config && (
        <Alert severity="info">
          No pricing configuration for this product. Click "New Draft" or "Clone to Draft" to start building plans.
        </Alert>
      )}

      {config && catalog.length > 0 && (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Feature Palette */}
            <FeaturePalette
              groups={catalog}
              restrictedKeys={allRestrictedKeys}
              isDraft={isDraft}
            />

            {/* Plan Cards */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap', overflow: 'auto', flex: 1, pb: 2 }}>
              {(config.plans || []).map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  configId={config.id}
                  catalogEntries={allCatalogEntries}
                  isDraft={isDraft}
                  onUpdatePlan={handleUpdatePlan}
                  onFeatureChange={handleFeatureChange}
                  onFeatureRemove={handleFeatureRemove}
                  onDeletePlan={handleDeletePlan}
                  workflows={workflows}
                />
              ))}

              {isDraft && (
                <Box
                  sx={{
                    minWidth: 200, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', border: '2px dashed', borderColor: 'divider',
                    borderRadius: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => setAddPlanDialog(true)}
                >
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <AddIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">New Plan</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeDrag && (
              <Chip
                label={activeDrag.displayName}
                color="primary"
                sx={{ cursor: 'grabbing', boxShadow: 3 }}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Add Plan Dialog */}
      <Dialog open={addPlanDialog} onClose={() => setAddPlanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Plan</DialogTitle>
        <DialogContent>
          <TextField label="Plan ID" required fullWidth sx={{ mt: 1, mb: 2 }}
            value={addPlanForm.planId} onChange={(e) => setAddPlanForm(f => ({ ...f, planId: e.target.value }))}
            placeholder="e.g., pro-monthly" />
          <TextField label="Name" required fullWidth sx={{ mb: 2 }}
            value={addPlanForm.name} onChange={(e) => setAddPlanForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Pro" />
          <TextField label="Price (cents)" type="number" fullWidth sx={{ mb: 2 }}
            value={addPlanForm.price_cents} onChange={(e) => setAddPlanForm(f => ({ ...f, price_cents: parseInt(e.target.value) || 0 }))} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Billing Interval</InputLabel>
              <Select value={addPlanForm.interval_type} onChange={(e) => setAddPlanForm(f => ({ ...f, interval_type: e.target.value }))} label="Billing Interval">
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="year">Annual</MenuItem>
                <MenuItem value="one_time">One-time</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Trial Days" type="number" fullWidth
              value={addPlanForm.trial_days} onChange={(e) => setAddPlanForm(f => ({ ...f, trial_days: parseInt(e.target.value) || 0 }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPlan} disabled={!addPlanForm.planId || !addPlanForm.name}>Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
